import {memo, useCallback, useEffect, useMemo, useState} from "react";
import PageBlock from "../../../components/PageBlock";
import Card from "../../../components/Card";
import {Button, CardBody} from "@material-tailwind/react";
import DataTable from "../../../components/DataTable/DataTable";
import TD from "../../../components/DataTable/TD";
import {API} from "../../../types/API";
import {DataTableRequest} from "../../../types/Requests/API/DataTableRequest";
import {AxiosResponse} from "axios";
import {useTranslation} from "react-i18next";
import {useToastContext} from "../../../types/contexts/ToastContext";
import {useErrorHandler} from "react-error-boundary";
import {ErrorHandler} from "../../../hooks/errorHandler";
import {APILink} from "../../../types/APILink";
import {useDispatch} from "react-redux";
import {setSidebarTitle} from "../../../reducers/sidebar";
import {setTitle} from "../../../hooks/setTitle";
import {useUserContext} from "../../../types/contexts/UserContext";
import {checkEnabled2FA} from "../../../hooks/checkEnabled2FA";
import {CallbacksMonitoringResponse} from "../../../types/Responses/CallbacksMonitoringResponse";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {solid} from "@fortawesome/fontawesome-svg-core/import.macro";
import DropdownInput from "../../../components/DropdownInput";
import moment from "moment";
import {FooterListItem} from "../../../components/Sidebar/FooterMenu";
import Loader from "../../../assets/icons/Loader";
import Forbidden from "../../../components/Forbidden";
import TableSkeleton from "../../../components/Skeletons/TableSkeleton";
import usePersistedDataTableRequest from "../../../hooks/usePersistedDataTableRequest";
import useComponentVisible from "../../../hooks/useComponentVisible";
import ReportingSubscriptionModal from "../../../components/reporting/ReportingSubscriptionModal";
import {ReportingFilterLookups} from "../../../utils/reportingFilters";
import {ChartPeriod} from "../../../types/ChartPeriod";
import DatePickerMenu from "../../../components/DatePickerMenu";
import FilterChips, {FilterChipDefinition} from "../../../components/FilterChips";

type CallbackFiltersState = {
    date_from: string;
    date_to: string;
    statuses: number[];
};

type CallbackMonitoringRequest = DataTableRequest & Partial<CallbackFiltersState> & {
    status?: number | number[];
};

const normalizeStatuses = (raw?: unknown): number[] => {
    const values: number[] = [];
    if (Array.isArray(raw)) {
        raw.forEach((entry) => {
            if (typeof entry === 'number') {
                values.push(entry);
                return;
            }
            if (typeof entry === 'string' && entry.length > 0) {
                const parsed = parseInt(entry, 10);
                if (!Number.isNaN(parsed)) {
                    values.push(parsed);
                }
            }
        });
    } else if (typeof raw === 'number') {
        values.push(raw);
    } else if (typeof raw === 'string' && raw.length > 0) {
        const parsed = parseInt(raw, 10);
        if (!Number.isNaN(parsed)) {
            values.push(parsed);
        }
    }
    return Array.from(new Set(values));
};

const CallbackMonitoring = () => {
    const {t} = useTranslation();
    const toast = useToastContext();
    const dispatch = useDispatch();
    const user = useUserContext();
    const handleError = useErrorHandler();
    const [callbacks, setCallbacks] = useState<CallbacksMonitoringResponse | null>(null);
    const [isDataTableLoading, setIsDataTableLoading] = useState<boolean>(false);
    const [isForbidden, setIsForbidden] = useState<boolean>(false);
    const [restartQueueLoading, setRestartQueueLoading] = useState<boolean>(false);
    const [restartingCallbackId, setRestartingCallbackId] = useState<number | null>(null);
    const dateFormat = 'YYYY-MM-DD';
    const defaultDateTo = useMemo(() => moment().format(dateFormat), [dateFormat]);
    const defaultDateFrom = useMemo(() => moment().subtract(7, 'days').format(dateFormat), [dateFormat]);
    const defaultRequest = useMemo<CallbackMonitoringRequest>(() => ({
        length: 25,
        page: 1,
        sort_by: 'created_at',
        sort_direction: 1,
        date_from: defaultDateFrom,
        date_to: defaultDateTo,
    }), [defaultDateFrom, defaultDateTo]);
    const [dataTableRequest, persistDataTableRequest] = usePersistedDataTableRequest<CallbackMonitoringRequest>(defaultRequest);
    const [filterDateFrom, setFilterDateFrom] = useState<string>(dataTableRequest.date_from ?? defaultDateFrom);
    const [filterDateTo, setFilterDateTo] = useState<string>(dataTableRequest.date_to ?? defaultDateTo);
    const [filterStatuses, setFilterStatuses] = useState<number[]>(() => {
        const persisted = dataTableRequest.statuses ?? dataTableRequest.status;
        return normalizeStatuses(persisted);
    });
    const subscriptionModal = useComponentVisible(false);
    const [subscriptionDefaults, setSubscriptionDefaults] = useState<{
        filters: Record<string, unknown>;
        typeName: string
    } | null>(null);
    const callbackReportTypeName = 'Callback report';

    useEffect(() => {
        setFilterDateFrom(dataTableRequest.date_from ?? defaultDateFrom);
    }, [dataTableRequest.date_from, defaultDateFrom]);

    useEffect(() => {
        setFilterDateTo(dataTableRequest.date_to ?? defaultDateTo);
    }, [dataTableRequest.date_to, defaultDateTo]);

    useEffect(() => {
        const persisted = dataTableRequest.statuses ?? dataTableRequest.status;
        setFilterStatuses(normalizeStatuses(persisted));
    }, [dataTableRequest.statuses, dataTableRequest.status]);

    const permissions = user?.user?.permissions ?? [];
    const canViewBody = permissions.includes('view callback body');
    const canRestartCallback = permissions.includes('restart callback');
    const canRestartQueue = permissions.includes('restart queue');
    const canCreateSubscription = permissions.includes('create reporting subscription');

    const statusOptions = useMemo<FooterListItem[]>(() => ([
        {name: t('admin.callbacks_monitoring.status.pending'), value: 0},
        {name: t('admin.callbacks_monitoring.status.started'), value: 1},
        {name: t('admin.callbacks_monitoring.status.failed'), value: 3},
    ]), [t]);

    const subscriptionMetadata = useMemo<ReportingFilterLookups>(() => ({
        statuses: statusOptions
            .filter((option) => option.value !== '')
            .map((option) => ({
                id: typeof option.value === 'number' ? option.value : parseInt(option.value as string, 10),
                name: option.name?.toString() ?? '',
            }))
    }), [statusOptions]);

    const cleanRequest = useCallback((request: CallbackMonitoringRequest): CallbackMonitoringRequest => {
        const payload = {...request};
        if (!payload.statuses || payload.statuses.length === 0) {
            delete payload.statuses;
        }
        if (typeof payload.status !== 'undefined') {
            delete payload.status;
        }
        return payload;
    }, []);

    const getFiltersPayload = useCallback((overrides?: Partial<CallbackFiltersState>): Partial<CallbackMonitoringRequest> => {
        const filters: CallbackFiltersState = {
            date_from: overrides?.date_from ?? filterDateFrom,
            date_to: overrides?.date_to ?? filterDateTo,
            statuses: overrides?.statuses ?? filterStatuses,
        };

        const payload: Partial<CallbackMonitoringRequest> = {
            date_from: filters.date_from,
            date_to: filters.date_to,
        };

        if (filters.statuses.length > 0) {
            payload.statuses = filters.statuses;
        }

        return payload;
    }, [filterDateFrom, filterDateTo, filterStatuses]);

    const sendRequest = useCallback((request: CallbackMonitoringRequest) => {
        setIsDataTableLoading(true);
        API.getCallbacks(cleanRequest(request)).then((response: AxiosResponse) => {
            setCallbacks(response.data as CallbacksMonitoringResponse);
            setIsDataTableLoading(false);
        }).catch((e) => {
            setIsDataTableLoading(false);
            ErrorHandler(e, `get${APILink.GET_CALLBACKS}`, toast, handleError, setIsForbidden);
        });
    }, [toast, handleError, cleanRequest]);

    const applyFilters = useCallback((overrides?: Partial<CallbackFiltersState>) => {
        const payload = {
            ...dataTableRequest,
            page: 1,
            ...getFiltersPayload(overrides),
        };
        persistDataTableRequest(payload);
        sendRequest(payload);
    }, [dataTableRequest, getFiltersPayload, sendRequest, persistDataTableRequest]);

    const refresh = useCallback(() => {
        const payload = {
            ...dataTableRequest,
            ...getFiltersPayload(),
        };
        sendRequest(payload);
    }, [dataTableRequest, getFiltersPayload, sendRequest]);

    const onDataTableChanged = useCallback((request: CallbackMonitoringRequest) => {
        const payload = {
            ...request,
            ...getFiltersPayload(),
        };
        persistDataTableRequest(payload);
        if (!request.search || request.search.length > 3) {
            sendRequest(!request.search ? {...payload, search: ''} : payload);
        }
    }, [getFiltersPayload, persistDataTableRequest, sendRequest]);

    useEffect(() => {
        setTitle(t('admin.callbacks_monitoring.title'));
        dispatch(setSidebarTitle(t('admin.callbacks_monitoring.title')));
        if (user && user.user) {
            checkEnabled2FA(user.user).then(() => {
                sendRequest({
                    ...dataTableRequest,
                    ...getFiltersPayload(),
                });
            }).catch();
        }
    }, [t, user, dispatch, sendRequest, dataTableRequest, getFiltersPayload]);

    const onStatusChanged = useCallback((value: string | number) => {
        if (value === '' || (typeof value === 'string' && value.length === 0)) {
            return;
        }
        const parsed = typeof value === 'number' ? value : parseInt(value, 10);
        if (!Number.isNaN(parsed)) {
            setFilterStatuses((prev) => prev.includes(parsed) ? prev : [...prev, parsed]);
        }
    }, []);

    const onStatusRemoved = useCallback((index: number) => {
        setFilterStatuses((prev) => {
            if (index < 0 || index >= prev.length) {
                return prev;
            }
            const next = [...prev];
            next.splice(index, 1);
            return next;
        });
    }, []);

    const removeStatusChip = useCallback((value: number) => {
        setFilterStatuses((prev) => {
            const index = prev.indexOf(value);
            if (index === -1) {
                return prev;
            }
            const next = [...prev];
            next.splice(index, 1);
            applyFilters({statuses: next});
            return next;
        });
    }, [applyFilters]);

    const resetDateFilters = useCallback(() => {
        setFilterDateFrom(defaultDateFrom);
        setFilterDateTo(defaultDateTo);
        applyFilters({
            date_from: defaultDateFrom,
            date_to: defaultDateTo,
        });
    }, [applyFilters, defaultDateFrom, defaultDateTo]);

    const restartQueueHandler = useCallback(() => {
        setRestartQueueLoading(true);
        API.restartCallbacksQueue().then((response: AxiosResponse) => {
            const restarted = response.data?.restarted ?? 0;
            if (restarted > 0) {
                toast?.show(t('admin.callbacks_monitoring.restart_queue.success', {count: restarted}), 'bg-teal-500');
            } else {
                toast?.show(t('admin.callbacks_monitoring.restart_queue.empty'), 'bg-teal-500');
            }
            setRestartQueueLoading(false);
            refresh();
        }).catch((e) => {
            setRestartQueueLoading(false);
            ErrorHandler(e, `post${APILink.RESTART_CALLBACKS_QUEUE}`, toast, handleError);
        });
    }, [toast, handleError, refresh, t]);

    const actionsPanel = useMemo(() => (
        <div className="flex flex-row gap-2 lg:justify-end">
            <Button className="bg-transparent text-black dark:text-white shadow-none px-0.5" onClick={() => refresh()}>
                <FontAwesomeIcon icon={solid('refresh')} className="w-12 h-3"/>
            </Button>
            {canRestartQueue && (
                <Button className="bg-black text-white flex items-center gap-2 px-3 py-2"
                        onClick={() => restartQueueHandler()}
                        disabled={restartQueueLoading}>
                    {restartQueueLoading ? <Loader width="w-4 h-4"/> : <FontAwesomeIcon icon={solid('rotate-right')}/>}
                    {t('admin.callbacks_monitoring.actions.restart_queue')}
                </Button>
            )}
            {canCreateSubscription && (
                <Button className="bg-black text-white px-3 py-2"
                        onClick={() => openSubscriptionModal()}>
                    {t('admin.reporting.subscriptions.create')}
                </Button>
            )}
        </div>
    ), [refresh, canRestartQueue, restartQueueHandler, restartQueueLoading, canCreateSubscription, t]);

    const openSubscriptionModal = useCallback(() => {
        setSubscriptionDefaults({
            filters: getFiltersPayload(),
            typeName: callbackReportTypeName
        });
        subscriptionModal.setIsComponentVisible(true);
    }, [getFiltersPayload, callbackReportTypeName, subscriptionModal]);

    const subscriptionModalUI = useMemo(() => {
        if (!subscriptionModal.isComponentVisible || !subscriptionDefaults) {
            return <></>;
        }
        return (
            <ReportingSubscriptionModal
                open={subscriptionModal.isComponentVisible}
                modalRef={subscriptionModal.ref}
                onClose={() => {
                    subscriptionModal.setIsComponentVisible(false);
                    setSubscriptionDefaults(null);
                }}
                defaultFilters={subscriptionDefaults.filters}
                defaultReportingTypeName={subscriptionDefaults.typeName}
                metadata={subscriptionMetadata}
            />
        );
    }, [subscriptionModal, subscriptionDefaults, subscriptionMetadata]);

    const restartCallback = useCallback((callbackId: number) => {
        setRestartingCallbackId(callbackId);
        API.restartCallback(callbackId).then(() => {
            toast?.show(t('admin.callbacks_monitoring.restart.success'), 'bg-teal-500');
            setRestartingCallbackId(null);
            refresh();
        }).catch((e) => {
            setRestartingCallbackId(null);
            ErrorHandler(e, `post${APILink.RESTART_CALLBACK.replace('$id', callbackId.toString())}`, toast, handleError);
        });
    }, [toast, handleError, refresh, t]);

    const onSetupDates = useCallback((from?: string, to?: string) => {
        setFilterDateFrom(from ?? filterDateFrom);
        setFilterDateTo(to ?? filterDateTo);
    }, [filterDateFrom, filterDateTo]);

    const filters = useMemo(() => (
        <div className="grid grid-cols-1 gap-0">
            <DatePickerMenu dateFormat={dateFormat} startDate={filterDateFrom} endDate={filterDateTo}
                            showCustomisationButton={false}
                            setEnd={setFilterDateTo}
                            setStart={setFilterDateTo}
                            showFilterButton={false}
                            onChanged={onSetupDates}
                            className="w-full"
                            defaultPeriod={ChartPeriod.CUSTOM}/>
            <DropdownInput buttonClassName="min-h-[auto!important]"
                           list={statusOptions}
                           multiple={true} searchable={true} withLabel={true}
                           name={t('admin.callbacks_monitoring.filters.status')}
                           selected={filterStatuses}
                           onRemove={(index) => onStatusRemoved(index)}
                           onChange={(value) => onStatusChanged(value)}/>
            <Button className="bg-black text-white px-4 py-2" onClick={() => applyFilters()}>
                {t('admin.callbacks_monitoring.filters.apply')}
            </Button>
        </div>
    ), [filterDateFrom, filterDateTo, filterStatuses, statusOptions, onStatusChanged, onStatusRemoved, applyFilters, t]);

    const filterChips = useMemo<FilterChipDefinition[]>(() => {
        const chips: FilterChipDefinition[] = [];
        if (filterStatuses.length) {
            const statusLabel = t('admin.callbacks_monitoring.filters.status');
            filterStatuses.forEach((statusValue) => {
                const option = statusOptions.find((item) => {
                    if (typeof item.value === 'number') {
                        return item.value === statusValue;
                    }
                    if (typeof item.value === 'string' && item.value.length > 0) {
                        const parsed = parseInt(item.value, 10);
                        return !Number.isNaN(parsed) && parsed === statusValue;
                    }
                    return false;
                });
                const optionLabel = option?.name?.toString() ?? statusValue.toString();
                chips.push({
                    key: `status-${statusValue}`,
                    label: `${statusLabel}: ${optionLabel}`,
                    onRemove: () => removeStatusChip(statusValue)
                });
            });
        }
        const hasCustomDateRange = filterDateFrom !== defaultDateFrom || filterDateTo !== defaultDateTo;
        if (hasCustomDateRange) {
            chips.push({
                key: 'date-range',
                label: `${t('admin.reporting.deposits.table.date')}: ${filterDateFrom} - ${filterDateTo}`,
                onRemove: () => resetDateFilters()
            });
        }
        return chips;
    }, [filterStatuses, statusOptions, t, removeStatusChip, filterDateFrom, filterDateTo, defaultDateFrom, defaultDateTo, resetDateFilters]);

    const headers = useMemo(() => {
        const base = [
            {name: t('admin.callbacks_monitoring.columns.created'), sortBy: 'created_at'},
            {name: t('admin.callbacks_monitoring.columns.url'), sortBy: 'url', className: 'text-left'},
        ];
        if (canViewBody) {
            base.push({name: t('admin.callbacks_monitoring.columns.body'), className: 'text-left', sortBy: 'body'});
        }
        base.push(
            {name: t('admin.callbacks_monitoring.columns.status'), sortBy: 'status'},
            {name: t('admin.callbacks_monitoring.columns.transaction'), sortBy: 'transaction_id'},
            {name: t('admin.callbacks_monitoring.columns.updated'), sortBy: 'updated_at'},
            {name: t('admin.callbacks_monitoring.columns.tries'), sortBy: 'tries'}
        );
        if (canRestartCallback) {
            base.push({name: t('admin.callbacks_monitoring.columns.actions'), sortBy: 'id'});
        }
        return base;
    }, [t, canViewBody, canRestartCallback]);

    const renderRows = useMemo(() => {
        if (!callbacks) {
            return <></>;
        }

        return callbacks.data.map((callback) => (
            <tr key={`callback-${callback.id}`}>
                <TD>{moment(callback.created_at).format('YYYY-MM-DD HH:mm:ss')}</TD>
                <TD className="text-left max-w-xs truncate">{callback.url ?? '-'}</TD>
                {canViewBody && (
                    <TD className="text-left">
                        <div
                            className="max-h-40 overflow-y-auto bg-blue-gray-50 dark:bg-blue-gray-900 dark:text-white rounded px-2 py-1 text-left whitespace-pre-wrap break-all">
                            {callback.body ?? '-'}
                        </div>
                    </TD>
                )}
                <TD>
                    <span
                        className="px-2 py-1 rounded-full bg-blue-gray-100 dark:bg-blue-gray-800 text-sm inline-block">
                        {callback.status.label}
                    </span>
                </TD>
                <TD>{callback.transaction ?? '-'}</TD>
                <TD>{moment(callback.updated_at).format('YYYY-MM-DD HH:mm:ss')}</TD>
                <TD>{callback.tries}</TD>
                {canRestartCallback && (
                    <TD>
                        <Button
                            className="bg-black text-white px-3 py-2"
                            onClick={() => restartCallback(callback.id)}
                            disabled={restartingCallbackId === callback.id}
                        >
                            {restartingCallbackId === callback.id ?
                                <Loader width="w-4 h-4"/> : t('admin.callbacks_monitoring.actions.restart')}
                        </Button>
                    </TD>
                )}
            </tr>
        ));
    }, [callbacks, canViewBody, canRestartCallback, restartCallback, restartingCallbackId, t]);

    if (isForbidden) {
        return <Forbidden isForbidden={isForbidden}/>;
    }

    return (
        <PageBlock>
            <Card className="mt-6 bg-white dark:bg-blue-gray-900 dark:text-gray-500">
                <CardBody>
                    {(!callbacks && isDataTableLoading) ? <TableSkeleton/> : (
                        <DataTable {...dataTableRequest}
                                   total_pages={callbacks?.pages.total ?? 1}
                                   count={callbacks?.count ?? 0}
                                   nodata={!callbacks || callbacks.data.length === 0}
                                   filters={filters}
                                   fullWidthFilters={<FilterChips className="-mt-6 mb-6" items={filterChips}/>}
                                   rightPanel={actionsPanel}
                                   inProgress={isDataTableLoading}
                                   onChange={onDataTableChanged}
                                   headers={headers}>
                            <tbody>
                            {callbacks && renderRows}
                            </tbody>
                        </DataTable>
                    )}
                </CardBody>
            </Card>
            {subscriptionModalUI}
        </PageBlock>
    );
};

export default memo(CallbackMonitoring);
